import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { ArrowRight, Clock, TrendingUp, Users, Zap, CheckCircle, Mail } from 'lucide-react';

const KILab = () => {
  const { t } = useLanguage();

  return (
    <>
      <SEOHead 
        title={t('kiLab.seo.title')}
        description={t('kiLab.seo.description')}
        keywords={t('kiLab.seo.keywords')}
        canonicalUrl="/leistungsangebot/ki-lab"
      />
      <div className="theme-brand min-h-screen bg-surface text-ink">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-[64ch] mb-16">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                {t('kiLab.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-ink-muted mb-8 max-w-4xl mx-auto leading-relaxed">
                {t('kiLab.hero.subtitle')}
              </p>
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-6 max-w-[62ch] mb-8">
                <p className="text-lg text-red-200 leading-relaxed">
                  {t('kiLab.hero.problem')}
                </p>
              </div>
              <ContactModal 
                buttonText={t('kiLab.hero.cta')}
                buttonContext="KI-Lab Hero"
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-8 py-4 text-lg font-semibold rounded-lg inline-flex items-center gap-2"
              >
                <ArrowRight className="h-5 w-5" />
              </ContactModal>
            </div>
          </div>
        </section>

        {/* Problem & Solution Section */}
        <section className="py-20 px-4 bg-surface-raised/50">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
                  {t('kiLab.problem.title')}
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-6 w-6 text-red-400 mt-1 flex-shrink-0" />
                    <p className="text-ink-muted text-lg">{t('kiLab.problem.point1')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-6 w-6 text-red-400 mt-1 flex-shrink-0" />
                    <p className="text-ink-muted text-lg">{t('kiLab.problem.point2')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="h-6 w-6 text-red-400 mt-1 flex-shrink-0" />
                    <p className="text-ink-muted text-lg">{t('kiLab.problem.point3')}</p>
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
                  {t('kiLab.solution.title')}
                </h2>
                <p className="text-ink-muted text-lg mb-6 leading-relaxed">
                  {t('kiLab.solution.description')}
                </p>
                <div className="bg-teal-900/20 border border-teal-800/50 rounded-lg p-6">
                  <p className="text-teal-200 text-lg font-medium">
                    {t('kiLab.solution.highlight')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4 Phases Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-[64ch] mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('kiLab.phases.title')}
              </h2>
              <p className="text-xl text-ink-muted max-w-[62ch]">
                {t('kiLab.phases.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Phase 1 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-8 text-center relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-brand text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    1
                  </div>
                </div>
                <Zap className="h-12 w-12 text-brand-400 mx-auto mb-4 mt-4" />
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-4">
                  {t('kiLab.phases.phase1.title')}
                </h3>
                <p className="text-ink-muted mb-6 leading-relaxed">
                  {t('kiLab.phases.phase1.description')}
                </p>
                <div className="text-sm text-brand-400 font-medium">
                  {t('kiLab.phases.phase1.duration')}
                </div>
              </div>

              {/* Phase 2 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-8 text-center relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-brand text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    2
                  </div>
                </div>
                <TrendingUp className="h-12 w-12 text-brand-400 mx-auto mb-4 mt-4" />
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-4">
                  {t('kiLab.phases.phase2.title')}
                </h3>
                <p className="text-ink-muted mb-6 leading-relaxed">
                  {t('kiLab.phases.phase2.description')}
                </p>
                <div className="text-sm text-brand-400 font-medium">
                  {t('kiLab.phases.phase2.duration')}
                </div>
              </div>

              {/* Phase 3 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-8 text-center relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-brand text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    3
                  </div>
                </div>
                <Users className="h-12 w-12 text-brand-400 mx-auto mb-4 mt-4" />
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-4">
                  {t('kiLab.phases.phase3.title')}
                </h3>
                <p className="text-ink-muted mb-6 leading-relaxed">
                  {t('kiLab.phases.phase3.description')}
                </p>
                <div className="text-sm text-orange-400 font-medium">
                  {t('kiLab.phases.phase3.optional')}
                </div>
              </div>

              {/* Phase 4 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-8 text-center relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-brand text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    4
                  </div>
                </div>
                <CheckCircle className="h-12 w-12 text-brand-400 mx-auto mb-4 mt-4" />
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-4">
                  {t('kiLab.phases.phase4.title')}
                </h3>
                <p className="text-ink-muted mb-6 leading-relaxed">
                  {t('kiLab.phases.phase4.description')}
                </p>
                <div className="text-sm text-green-400 font-medium">
                  {t('kiLab.phases.phase4.result')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 bg-surface-raised/50">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-[64ch] mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('kiLab.benefits.title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t('kiLab.benefits.benefit1.title')}
                    </h3>
                    <p className="text-ink-muted">
                      {t('kiLab.benefits.benefit1.description')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t('kiLab.benefits.benefit2.title')}
                    </h3>
                    <p className="text-ink-muted">
                      {t('kiLab.benefits.benefit2.description')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t('kiLab.benefits.benefit3.title')}
                    </h3>
                    <p className="text-ink-muted">
                      {t('kiLab.benefits.benefit3.description')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {t('kiLab.benefits.benefit4.title')}
                    </h3>
                    <p className="text-ink-muted">
                      {t('kiLab.benefits.benefit4.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Differentiator Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="bg-teal-900/20 border border-teal-800/50 rounded-xl p-12">
              <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
                {t('kiLab.differentiator.title')}
              </h2>
              <p className="text-xl text-teal-200 mb-8 leading-relaxed">
                {t('kiLab.differentiator.description')}
              </p>
              <div className="grid md:grid-cols-2 gap-8 text-left">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('kiLab.differentiator.not.title')}
                  </h3>
                  <p className="text-ink-muted">
                    {t('kiLab.differentiator.not.description')}
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('kiLab.differentiator.is.title')}
                  </h3>
                  <p className="text-ink-muted">
                    {t('kiLab.differentiator.is.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Target Audience Section */}
        <section className="py-20 px-4 bg-surface-raised/50">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-[64ch] mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('kiLab.audience.title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Audience 1 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-6 text-center">
                <div className="h-12 w-12 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <p className="text-ink-muted leading-relaxed">
                  {t('kiLab.audience.point1')}
                </p>
              </div>

              {/* Audience 2 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-6 text-center">
                <div className="h-12 w-12 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <p className="text-ink-muted leading-relaxed">
                  {t('kiLab.audience.point2')}
                </p>
              </div>

              {/* Audience 3 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-6 text-center">
                <div className="h-12 w-12 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <p className="text-ink-muted leading-relaxed">
                  {t('kiLab.audience.point3')}
                </p>
              </div>

              {/* Audience 4 */}
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-6 text-center">
                <div className="h-12 w-12 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">4</span>
                </div>
                <p className="text-ink-muted leading-relaxed">
                  {t('kiLab.audience.point4')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('kiLab.cta.title')}
            </h2>
            <p className="text-xl text-ink-muted mb-12 leading-relaxed">
              {t('kiLab.cta.description')}
            </p>

            <div className="max-w-xl mx-auto mb-12">
              <div className="bg-surface-raised/50 border border-hairline rounded-xl p-8">
                <Mail className="h-12 w-12 text-brand-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-4">
                  {t('kiLab.cta.email.title')}
                </h3>
                <p className="text-ink-muted mb-4">
                  {t('kiLab.cta.email.description')}
                </p>
                <ContactModal 
                  buttonText={t('kiLab.cta.email.button')}
                  buttonContext="KI-Lab Contact"
                  className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-2 rounded-lg font-semibold"
                />
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-6 mb-8">
              <p className="text-blue-200 text-lg">
                {t('kiLab.cta.mvpLink.text')}{' '}
                <a 
                  href="/leistungsangebot/ki-mvp" 
                  className="text-blue-400 hover:text-blue-300 underline font-semibold"
                >
                  {t('kiLab.cta.mvpLink.linkText')}
                </a>
              </p>
            </div>

            <ContactModal 
              buttonText={t('kiLab.cta.mainButton')}
              buttonContext="KI-Lab Main CTA"
              className="bg-brand hover:bg-brand-600 text-ink-onBrand px-8 py-4 text-lg font-semibold rounded-lg inline-flex items-center gap-2"
            >
              <ArrowRight className="h-5 w-5" />
            </ContactModal>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default KILab;